import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';
import mugBg from '../images/user-background.png';
import { PROFILE_IMAGES as IMAGES, CHAR_SCALES } from '../constants/profileImages';

function EuIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'home':  return <svg {...p}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/></svg>;
        case 'back':  return <svg {...p}><path d="M15 18l-6-6 6-6"/></svg>;
        case 'check': return <svg {...p}><path d="M20 6L9 17l-5-5"/></svg>;
        case 'info':  return <svg {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
        case 'male':  return <svg {...p}><circle cx="10" cy="14" r="5"/><path d="M19 5l-5.5 5.5"/><path d="M14 5h5v5"/></svg>;
        case 'female':return <svg {...p}><circle cx="12" cy="9" r="5"/><path d="M12 14v7"/><path d="M9 18h6"/></svg>;
        case 'edit':  return <svg {...p}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>;
        default: return null;
    }
}

export default function EditUser() {
    const { t } = useTranslation();
    const [profileInfo, setProfileInfo] = useState({ username: '', average: 0, wines: 0, profile: 0, id: 0 });
    const [actual, setActual] = useState(0);
    const [animClass, setAnimClass] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const animLock = useRef(false);
    const navigate = useNavigate();
    const profiles = t('profiles', { returnObjects: true });

    useEffect(() => {
        const userLogged = localStorage.getItem('userLogged');
        if (userLogged) {
            const userInfo = JSON.parse(userLogged);
            setProfileInfo(userInfo);
            setActual(userInfo.profile ?? 0);
        }
        requestAnimationFrame(() => setReady(true));
    }, []);

    const cambiarImagen = useCallback((direccion) => {
        if (animLock.current) return;
        animLock.current = true;
        const exitClass = direccion > 0 ? 'up-char-exit-left' : 'up-char-exit-right';
        const enterClass = direccion > 0 ? 'up-char-enter-right' : 'up-char-enter-left';
        setAnimClass(exitClass);
        setTimeout(() => {
            setActual(prev => {
                let next = parseInt(prev) + direccion;
                if (next > 13) next = next % 14;
                if (next < 0) next = (next + 14) % 14;
                return next;
            });
            setAnimClass(enterClass);
            setTimeout(() => { setAnimClass(''); animLock.current = false; }, 440);
        }, 160);
    }, []);

    const handleConfirm = () => {
        setSaving(true);
        const xhr = new XMLHttpRequest();
        const data = new FormData();
        data.append('profile', actual);
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/users/${profileInfo.id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                setSaving(false);
                if (this.status === 200) {
                    const user = JSON.parse(this.responseText);
                    localStorage.setItem('userLogged', JSON.stringify({
                        username: user.username, profile: user.profile,
                        wines: user.wines, average: user.average, id: user.id, email: user.email,
                    }));
                    navigate('/');
                }
            }
        };
        xhr.send(data);
    };

    const isEven = actual % 2 === 0;
    const characterNo = Math.floor(actual / 2) + 1;
    const currentProfile = Array.isArray(profiles) ? profiles[actual] : null;

    const setGender = (wantFemale) => {
        const base = Math.floor(actual / 2) * 2;
        const target = wantFemale ? base + 1 : base;
        if (target === actual) return;
        cambiarImagen(wantFemale ? 1 : -1);
    };

    return (
        <div className={`up-stage${ready ? ' up-ready' : ''}`}>
            <div className="up-stage-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="up-vignette" />
            <div className="up-grain" />

            <div className="up-topbar">
                <div className="up-nav-group">
                    <button className="up-icon-btn" onClick={() => navigate('/')} aria-label={t('common.home')}>
                        <EuIcon name="home" size={18} />
                    </button>
                </div>
                <div className="up-topbar-title">
                    Wine Gang
                    <small>{t('edit_user.title')}</small>
                </div>
                <div className="up-nav-group">
                    <button className="up-icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                        <EuIcon name="back" size={18} />
                    </button>
                </div>
            </div>

            <div className="eu-picker-body">
                <div className="eu-picker-portrait-col">
                    <div className="up-portrait-wrap">
                        <div className="up-portrait-frame" style={{ backgroundImage: `url(${mugBg})` }}>
                            <div style={{
                                position: 'absolute', inset: 0,
                                transform: `scale(${CHAR_SCALES[actual]})`,
                                transformOrigin: 'bottom center',
                            }}>
                                <img
                                    src={IMAGES[actual]}
                                    alt={t('edit_user.character_alt')}
                                    className={`up-portrait-img${animClass ? ' ' + animClass : ''}`}
                                />
                            </div>
                            <div className="up-portrait-tape">EVIDENCE · WPD</div>
                            <div className="up-portrait-id">
                                <span>{t('edit_user.character_counter', { number: characterNo })}</span>
                                <span>WINE TASTING</span>
                            </div>
                        </div>

                        <div className="up-portrait-controls up-controls-active">
                            <div className="up-character-counter">
                                {t('edit_user.character_counter', { number: characterNo })}
                            </div>
                            <button className="up-arrow-btn up-arrow-left" onClick={() => cambiarImagen(-2)} aria-label={t('edit_user.prev_character')}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                            <button className="up-arrow-btn up-arrow-right" onClick={() => cambiarImagen(2)} aria-label={t('edit_user.next_character')}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                            <div className="up-gender-buttons">
                                <button
                                    className={`up-gender-btn${isEven ? ' up-gender-active' : ''}`}
                                    onClick={() => setGender(false)}
                                    title={t('edit_user.male')}
                                    aria-label={t('edit_user.male')}
                                >
                                    <EuIcon name="male" size={16} />
                                </button>
                                <button
                                    className={`up-gender-btn${!isEven ? ' up-gender-active' : ''}`}
                                    onClick={() => setGender(true)}
                                    title={t('edit_user.female')}
                                    aria-label={t('edit_user.female')}
                                >
                                    <EuIcon name="female" size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="up-portrait-meta eu-picker-meta">
                        <span className="up-badge"><span className="up-dot" />{t('common.wine_police')}</span>
                        {currentProfile && <p>"{currentProfile.title}"</p>}
                    </div>
                </div>

                <div className="eu-picker-actions">
                    <div className="eu-picker-actions-primary">
                        <button className="up-action-btn up-action-primary" onClick={handleConfirm} disabled={saving}>
                            <EuIcon name="check" size={16} />
                            {saving ? t('common.loading') : t('common.confirm')}
                        </button>
                    </div>
                    <div className="eu-picker-actions-secondary">
                        {currentProfile && (
                            <button className="up-action-btn" onClick={() => setShowInfo(true)}>
                                <EuIcon name="info" size={16} />
                                {t('edit_user.information')}
                            </button>
                        )}
                        <Link to="/edit-data" className="up-action-btn" style={{ textDecoration: 'none' }}>
                            <EuIcon name="edit" size={16} />
                            {t('edit_user.edit_data')}
                        </Link>
                    </div>
                </div>
            </div>

            {showInfo && currentProfile && (
                <div className="eu-info-overlay" onClick={() => setShowInfo(false)}>
                    <div className="eu-info-panel" onClick={e => e.stopPropagation()}>
                        <h2 className="eu-info-title">{currentProfile.title}</h2>
                        <p className="eu-info-text">{currentProfile.description}</p>
                        <button className="up-action-btn up-action-primary eu-info-close-btn" onClick={() => setShowInfo(false)}>
                            {t('common.close')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
